/**
 * Full screen flow of the automatic album, shared by the desktop and mobile editors : the
 * confirmation, then two steps of a PopupPanelNavigation :
 *  - magic.TitleStep   : the album title, helped by the TitleSuggester proposals
 *  - magic.PlacingStep : the placement itself with its progress, closes the panel when done
 *
 * Two ways in :
 *  - from the editor ("place-photos-chronologically") : the confirmation first, "show-editor" when done
 *  - the magic creation (/create/magic, mode "creating-ia") : a step of the Main right after the photo
 *    selection. No confirmation, the title comes first ; leaving before the placement returns to the
 *    photo selection ("previous-step"), the placed album opens the editor.
 *
 * The steps share one object { model, options : { title }, placing, placed } as their data.
 */
fx.ns("shared.screens", class MagicAlbumPanel extends fx.components.PopupPanelNavigation {

    constructor()
    {
        super();

        this.destroyOnSwitch = true;

        // magic creation : a step among the Main's ones. The panel itself appears at once (no effect,
        // the title step slides in like the other steps do) : the popup's own "scroll" effect would
        // move the children while it plays and leave a step added meanwhile above the screen, and any
        // effect on the panel is that much empty screen before the title step shows. Leaving slides
        // like the other steps.
        if (this.creating)
        {
            this.mode = "";   // no "Fermer" button : the back button returns to the photos
            this.showEffect = "";   // an unknown name would be refused and leave the popup's "scroll"
            this.hideEffect = "hslidein";
        }
    }

    // magic creation : the album is being created for these photos, nothing to confirm
    get creating()
    {
        return fx.get("mode") == "creating-ia";
    }

    /**
     * The confirmation : the offer across the whole box, a bigger portrait on a phone. The box
     * sizes and the button widths are the ones the screen has always used ; everything inside is
     * laid out by the stylesheet.
     */
    get confirmMetrics()
    {
        if (fx.browser.isDesktop)
            return { width : 600, height : 430, person : 52, button : 280, cancel : 160 };

        return { width : "100%", height : 545, person : 88, button : 260, cancel : 0 };
    }

    /** What the automatic composition promises, one line each. */
    get benefits()
    {
        return [
            fx.text("Ordre chronologique respecté"),
            fx.text("Chaque page modifiable"),
            fx.text("Prêt en quelques secondes")
        ];
    }

    create()
    {
        super.create();

        if (this.creating)
            return;

        let metrics = this.confirmMetrics;

        this.container = new fx.components.Box("container confirm");

        // ---- the offer
        let intro = new fx.components.Box("intro");

        let eyebrow = new fx.components.Box("eyebrow");
        this.person = new shared.component.Person(fx.get("assets").personSure);
        eyebrow.addChild(this.person, { width : metrics.person, height : metrics.person });
        eyebrow.addChild(new fx.components.Text({ text : fx.text("Composition intelligente"), className : "eyebrow-label" }));
        intro.addChild(eyebrow);

        this.title = new fx.components.Text({ text : fx.text("Je crée votre album pour vous !"), className : "title" });
        intro.addChild(this.title);

        this.text = new fx.components.Text({ text : fx.text("Vos photos rangées par date, posées sur des pages équilibrées. Vous n'avez plus qu'à relire."), className : "text" });
        intro.addChild(this.text);

        let benefits = new fx.components.Box("benefits");
        this.benefits.forEach((label) => benefits.addChild(new fx.components.Text({ text : '<span class="check"></span><span class="label">' + label + '</span>', className : "benefit" })));
        intro.addChild(benefits);

        // on a computer, the way to decline first and the offer beside it, the eye landing on the
        // filled button last. A phone has no room for a second button on that line : its panel is
        // left with the cross.
        let actions = new fx.components.Box("actions");

        if (metrics.cancel)
        {
            this.cancelButton = new fx.components.Button({ label : fx.text("Non merci") });
            actions.addChild(this.cancelButton, { width : metrics.cancel, height : 54 });
        }

        this.okButton = new fx.components.Button({ label : fx.text("Composer mon album"), type : "filled" });
        actions.addChild(this.okButton, { width : metrics.button, height : 54 });

        intro.addChild(actions);

        // the album is already worked on : said plainly, once, centred under the offer button
        // (the stylesheet shifts it past the declining one). It informs, the way back is the
        // panel's own "Fermer".
        if (this.data.creation.hasContent)
            intro.addChild(new fx.components.Text({ text : fx.text("Le contenu de votre album va être remplacé"), className : "footnote" }));

        this.container.addChild(intro);

        this.addChild(this.container, { width : metrics.width, height : metrics.height, top : "center", left : "center" });
    }

    onReady()
    {
        super.onReady();

        if (this.creating)
        {
            this._start();
            return;
        }

        // the diagonal split of the design belongs to this view only : the steps that follow keep
        // their own background, so the panel wears it while the confirmation is up (see steps())
        this.addClass("confirming");
        this.toggleClass("phone", !fx.browser.isDesktop);

        this.on("click", this._onConfirm, this.okButton);

        if (this.cancelButton)
            this.on("click", this.close, this.cancelButton);
    }

    // the steps are only added once the panel's show effect, if any, is over : an effect such as
    // "scroll" positions the children when it starts and would leave a later one off screen
    _start()
    {
        if (this.animating)
        {
            this.delay(this._start, 50);
            return;
        }

        this._onConfirm();
    }

    _onConfirm()
    {
        this._stepData = {
            model : this.data,
            options : { title : "" },
            placing : false,
            placed : false
        };

        this.steps(["shared.screens.magic.TitleStep", this._stepData], ["shared.screens.magic.PlacingStep", this._stepData]);
    }

    // the confirmation is hidden while the steps are shown (its illustration would show through),
    // and the panel drops its background with it
    steps()
    {
        if (this.container)
            this.container.hide();

        this.removeClass("confirming");

        super.steps.apply(this, arguments);
    }

    // back from the first step : the confirmation again, or the photo selection while creating
    _resetNavigation()
    {
        if (this.creating)
        {
            this.data.event("previous-step");
            return;
        }

        super._resetNavigation();

        if (this.container)
        {
            this.addClass("confirming");
            this.container.show();
        }
    }

    /**
     * The steps showing no way back : the placement, which cannot be undone, and, on the phone
     * editors (mobile and mobileapp), the title step opened from the editor — the panel is left
     * with its "Fermer" cross there. The magic creation keeps that button on every editor : its
     * panel carries no cross, and it is the only way back to the photo selection.
     */
    _hidesBack(className)
    {
        if (/PlacingStep$/.test(className))
            return true;

        return fx.browser.isMobile && !this.creating && /TitleStep$/.test(className);
    }

    // no way back once the photos are being placed. While creating, the first step shows at once
    // with the panel instead of sliding in : nothing to wait for after the photo selection.
    _showStep(index, data)
    {
        let instant = this.creating && index == 0 && !(this._navigationData[index] && this._navigationData[index].instance);
        let effects = fx.effectsEnabled;

        if (instant)
            fx.effectsEnabled = false;

        try
        {
            super._showStep(index, data);
        }
        finally
        {
            fx.effectsEnabled = effects;
        }

        if (this._navigationData[index] && this._hidesBack(this._navigationData[index].className))
            this.back.hide();
    }

    close()
    {
        if (this._stepData && this._stepData.placing)
            return;

        if (this.creating && !(this._stepData && this._stepData.placed))
        {
            this.data.event("previous-step");
            return;
        }

        // the album is built : the editor opens on its layouts, the "Thématique" tab showing the
        // covers proposed for it first. A flag as well as an event : in the magic creation the
        // editor, and its MediaTabs, are only built by show-editor.
        let placed = !!(this._stepData && this._stepData.placed);
        if (placed)
            this.data.showThematicLayouts = true;

        this.data.event("show-editor");

        if (placed)
            this.data.event("show-thematic-layouts");
    }
});
